import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Chip, Spinner } from '@structural-climbing/ui'

export interface SignedDocument {
  id: string
  documentId: string
  title: string
  version: number
  signedAt: string
  /** The child the member signed this for; null for their own signature. */
  child: { id: string; name: string } | null
  viewable: boolean
  /** Signed again since, after a name on the certificate changed. Kept as a record. */
  superseded?: boolean
}

interface Props {
  userId: string
}

/**
 * Every document a member has signed at this gym, including the ones they
 * signed on behalf of each child. "View" opens what the join-request review
 * opens: the signed PDF with its certificate page, or the drawn signature when
 * the document was an image and no PDF was produced.
 */
export function SignedDocumentsTab({ userId }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [documents, setDocuments] = useState<SignedDocument[] | null>(null)
  const [error, setError] = useState(false)
  const [openingId, setOpeningId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/members/${encodeURIComponent(userId)}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        if (!res.ok) {
          setError(true)
          return
        }
        const body = (await res.json()) as { documents?: SignedDocument[] }
        if (!cancelled) setDocuments(body.documents ?? [])
      } catch {
        if (!cancelled) setError(true)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // getAccessTokenSilently is stable per Auth0 SDK contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, apiUrl])

  async function handleView(doc: SignedDocument) {
    // Open the window before the fetch: after an await the click no longer
    // counts as a user gesture and Safari blocks the popup.
    const win = window.open('', '_blank')
    setOpeningId(doc.id)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${apiUrl}/members/${encodeURIComponent(userId)}/documents/${encodeURIComponent(doc.id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        win?.close()
        return
      }
      const url = URL.createObjectURL(await res.blob())
      if (win) win.location.href = url
      else window.open(url, '_blank')
    } catch {
      win?.close()
    } finally {
      setOpeningId(null)
    }
  }

  if (error) return <p className="text-sm text-danger">{t('common.errorDefault')}</p>
  if (documents === null)
    return (
      <div className="flex justify-center py-8">
        <Spinner size="sm" color="primary" />
      </div>
    )
  if (documents.length === 0)
    return <p className="text-sm text-foreground/30">{t('members.noSignedDocuments')}</p>

  return (
    <div className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="flex items-center justify-between gap-3 rounded-lg bg-content2/30 px-3 py-2"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-medium text-foreground/80">{doc.title}</p>
              {doc.child && (
                <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                  {t('members.signedForChild', { name: doc.child.name })}
                </span>
              )}
              {doc.superseded && (
                <Chip size="sm" variant="flat" className="shrink-0">
                  {t('members.signedReplaced')}
                </Chip>
              )}
            </div>
            <p className="text-xs text-foreground/40">
              {t('members.signedOn', {
                version: doc.version,
                date: new Date(doc.signedAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }),
              })}
            </p>
          </div>
          {doc.viewable && (
            <Button
              size="sm"
              variant="flat"
              isLoading={openingId === doc.id}
              isDisabled={openingId !== null}
              onClick={() => void handleView(doc)}
            >
              {t('members.viewSignedDocument')}
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}
